/**
 * ValidationStage Component
 *
 * Stage 3 of the import wizard - displays file validation results and preview.
 * Shows loading state during validation, errors if validation fails,
 * or preview table with file metadata if validation succeeds.
 *
 * @module components/import/stages
 */

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { ImportAction, ValidationResult } from '@/components/import/types';
import { formatFileSize } from '@/components/import/utils';
import { Loader } from '@/components/Loader';

/**
 * Props for ValidationStage component
 */
export interface ValidationStageProps {
  /** Selected import action from stage 1 */
  selectedAction: ImportAction;
  /** Uploaded file from stage 2 */
  uploadedFile: File;
  /** Validation result (null if still validating) */
  validationResult: ValidationResult | null;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Callback to return to previous stage */
  onBack: () => void;
  /** Callback to proceed with import */
  onImport: () => void;
}

/**
 * ValidationStage Component
 *
 * Displays file validation results with three possible states:
 * 1. Loading - Shows spinner while validation in progress
 * 2. Error - Shows error message if validation fails
 * 3. Success - Shows file preview table and metadata
 *
 * @param props - Component props
 * @returns Validation stage JSX
 */
export function ValidationStage({
  selectedAction,
  uploadedFile,
  validationResult,
  isValidating,
  onBack,
  onImport,
}: ValidationStageProps) {
  // Loading state
  if (isValidating) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader />
        <p className="text-chariot-text-secondary mt-4">Validating file...</p>
      </div>
    );
  }

  // Error state
  if (validationResult && !validationResult.valid) {
    return (
      <>
        <h3 className="text-chariot-text-primary text-lg font-bold">
          Validation Failed
        </h3>
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="size-6 flex-shrink-0 text-red-500" />
            <div>
              <h4 className="font-semibold text-red-500">Validation Error</h4>
              <p className="text-chariot-text-primary mt-1 text-sm">
                {validationResult.error}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Success state with preview
  return (
    <>
      <h3 className="text-chariot-text-primary text-lg font-bold">
        Preview & Validate
      </h3>

      {/* File metadata */}
      {validationResult?.metadata && (
        <div className="bg-chariot-background-secondary rounded-lg p-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-chariot-text-secondary">File</p>
              <p className="text-chariot-text-primary truncate font-medium">
                {validationResult.metadata.fileName}
              </p>
            </div>
            {validationResult.metadata.rowCount !== undefined && (
              <div>
                <p className="text-chariot-text-secondary">Rows</p>
                <p className="text-chariot-text-primary font-medium">
                  {validationResult.metadata.rowCount.toLocaleString()}
                </p>
              </div>
            )}
            <div>
              <p className="text-chariot-text-secondary">Size</p>
              <p className="text-chariot-text-primary font-medium">
                {formatFileSize(validationResult.metadata.fileSize)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview table */}
      {validationResult?.preview &&
        validationResult.preview.rows.length > 0 && (
          <div>
            <h4 className="text-chariot-text-primary mb-2 font-semibold">
              Preview (first {validationResult.preview.rows.length} rows)
            </h4>
            <div className="border-chariot-stroke overflow-hidden rounded-lg border">
              <div className="scrollbar-dark max-h-[400px] overflow-auto">
                <table className="w-full text-sm">
                  {/* Table headers */}
                  {validationResult.preview.headers && (
                    <thead className="bg-chariot-background-secondary sticky top-0">
                      <tr>
                        {validationResult.preview.headers.map((header, i) => (
                          <th
                            key={i}
                            className="text-chariot-text-primary border-chariot-stroke border-b px-4 py-2 text-left font-medium"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  {/* Table body */}
                  <tbody className="divide-chariot-stroke divide-y">
                    {validationResult.preview.rows.map((row, i) => (
                      <tr
                        key={i}
                        className="hover:bg-chariot-background-secondary"
                      >
                        {typeof row === 'object' && row !== null ? (
                          Object.values(row).map((cell, j) => (
                            <td
                              key={j}
                              className="text-chariot-text-primary max-w-xs px-4 py-2"
                            >
                              <div className="truncate" title={String(cell)}>
                                {String(cell)}
                              </div>
                            </td>
                          ))
                        ) : (
                          <td className="text-chariot-text-primary max-w-xs px-4 py-2">
                            <div className="truncate" title={String(row)}>
                              {String(row)}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
    </>
  );
}
